'use client'

import { ChevronLeft, Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'

export type ShareRoomInviteMessageFooterProps = {
  contentName?: string
  message: string
  onMessageChange: (value: string) => void
  isInternalInRoomTenant: boolean
  domainJoinEnabled: boolean
  createShareableLink: boolean
  onCreateShareableLinkCheckedChange: (checked: boolean) => void | Promise<void>
  generatedLink: string | null
  onCopyGeneratedShareableLink: () => void
  showPreview: boolean
  onTogglePreview: () => void
  /** When true, show wizard back + wide send; otherwise single full-width send. */
  communityWizardReviewLayout: boolean
  onCommunityWizardBack: () => void
  onSubmit: () => void
  isSendingInvites: boolean
  canSubmit: boolean
}

export function ShareRoomInviteMessageFooter({
  contentName,
  message,
  onMessageChange,
  isInternalInRoomTenant,
  domainJoinEnabled,
  createShareableLink,
  onCreateShareableLinkCheckedChange,
  generatedLink,
  onCopyGeneratedShareableLink,
  showPreview,
  onTogglePreview,
  communityWizardReviewLayout,
  onCommunityWizardBack,
  onSubmit,
  isSendingInvites,
  canSubmit,
}: ShareRoomInviteMessageFooterProps) {
  const sendLabel =
    isSendingInvites
      ? contentName
        ? 'Sharing Content...'
        : 'Sending Invitations...'
      : contentName
        ? 'Share Content'
        : 'Send Invitations'

  return (
    <div className="space-y-4">
      <Textarea
        placeholder={
          contentName
            ? 'Personalize your message (optional).'
            : 'Personalize your invitation message (optional).'
        }
        value={message}
        onChange={(e) => onMessageChange(e.target.value)}
        rows={3}
        className="mt-4"
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-2">
          {isInternalInRoomTenant && !domainJoinEnabled && (
            <>
              <Switch
                id="create-shareable-link"
                checked={createShareableLink}
                onCheckedChange={(c) => void onCreateShareableLinkCheckedChange(c === true)}
              />
              <label htmlFor="create-shareable-link" className="text-sm">
                {contentName ? 'Create content link' : 'Create shareable link'}
              </label>
            </>
          )}
        </div>
        {isInternalInRoomTenant && (
          <Button variant="outline" type="button" onClick={onTogglePreview}>
            <Eye className="mr-2 h-4 w-4" />
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>
        )}
      </div>

      {isInternalInRoomTenant && !domainJoinEnabled && createShareableLink && generatedLink && (
        <div className="mt-2 space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
          <div>
            <p className="text-sm font-medium text-slate-900">
              {contentName ? 'Content link' : 'Shareable link'}
            </p>
            {contentName && (
              <p className="mt-1 text-xs text-slate-600">
                Recipients will open this content inside the room.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Input value={generatedLink} readOnly className="font-mono text-xs" />
            <Button variant="outline" type="button" onClick={onCopyGeneratedShareableLink}>
              Copy
            </Button>
          </div>
        </div>
      )}

      {communityWizardReviewLayout ? (
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={onCommunityWizardBack} className="w-full sm:w-auto">
            <ChevronLeft className="mr-1 h-4 w-4" aria-hidden />
            Back
          </Button>
          <Button
            type="button"
            className="w-full sm:min-w-[200px]"
            onClick={onSubmit}
            disabled={!canSubmit || isSendingInvites}
          >
            {sendLabel}
          </Button>
        </div>
      ) : (
        <Button type="button" className="mt-4 w-full" onClick={onSubmit} disabled={!canSubmit || isSendingInvites}>
          {sendLabel}
        </Button>
      )}
    </div>
  )
}
